// src/views/LoginView.vue
<template>
  <div class="login-container">
    <div class="login-overlay" :style="{ backgroundImage: backgroundImageUrl }"></div>
    
    <div class="login-card">
      <div class="login-branding">
        <img :src="tenantStore.logoUrl || '@/assets/images/opsolver-logo.png'" :alt="tenantStore.tenantName" class="login-logo">
        <h1 class="company-name">{{ tenantStore.tenantName }}</h1>
        <div class="tagline">Gestion du personnel</div>
      </div>
      
      <div class="login-form">
        <h2>Connexion</h2>
        <p class="welcome-text">Bienvenue ! Veuillez vous connecter pour accéder à votre compte.</p>
        
        <div class="input-group">
          <div class="input-icon">
            <span class="material-icons">person</span>
          </div>
          <input 
            type="text" 
            id="username" 
            v-model="username" 
            placeholder="Nom d'utilisateur"
            class="form-control"
            :disabled="loading"
            @keyup.enter="handleLogin"
          >
        </div>
        
        <div class="input-group">
          <div class="input-icon">
            <span class="material-icons">lock</span>
          </div>
          <input 
            type="password" 
            id="password" 
            v-model="password" 
            placeholder="Mot de passe"
            class="form-control"
            :disabled="loading"
            @keyup.enter="handleLogin"
          >
        </div>
        
        <div class="remember-forgot">
          <label class="remember-me">
            <input type="checkbox"> Se souvenir de moi
          </label>
          <a href="#" class="forgot-password">Mot de passe oublié ?</a>
        </div>
        
        
        <div v-if="error" class="error-message">
          <span class="material-icons">error</span>
          {{ error }}
        </div>
        
        <button 
          @click="handleLogin" 
          class="login-button"
          :disabled="loading"
          :class="{ 'loading': loading }"
        >
          <span v-if="loading" class="button-content">
            <span class="spinner"></span>
            Connexion en cours...
          </span>
          <span v-else class="button-content">
            <span class="material-icons">login</span>
            Se connecter
          </span>
        </button>
        <!-- Le footer est maintenant dans le formulaire -->
        <div class="login-footer">
          <div class="footer-content">
            <p>&copy; {{ currentYear }} OpSolver. Tous droits réservés.</p>
            <button @click="goToHome" class="home-link" type="button">
              <span class="material-icons">home</span>
              Accueil
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useTenantStore } from '@/stores/tenant'

const router = useRouter()
const authStore = useAuthStore()
const tenantStore = useTenantStore()

const username = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')

// Année courante pour le copyright
const currentYear = computed(() => new Date().getFullYear())

// Image de fond dynamique
const backgroundImageUrl = computed(() => {
  const bgImage = tenantStore.bgImageUrl
  const defaultImage = "url('@/assets/images/slider-apcsc-1.jpg')"
  return bgImage ? `url('${bgImage}')` : defaultImage
})

// Charger seulement le branding au montage (pas la config complète)
onMounted(async () => {
  try {
    await tenantStore.loadBranding()
  } catch (error) {
    console.log('Aucun branding personnalisé trouvé, utilisation des valeurs par défaut')
  }
})

const goToHome = () => {
  router.push('/')
}

const handleLogin = async () => {
  if (!username.value || !password.value) {
    error.value = 'Veuillez remplir tous les champs'
    return
  }
  
  loading.value = true
  const success = await authStore.login(username.value, password.value)
  
  if (success) {
    // Charger la configuration du tenant après connexion réussie
    await tenantStore.loadConfig()
    router.push('/dashboard')
  } else {
    error.value = authStore.error || 'Erreur lors de la connexion'
  }
  
  loading.value = false
}
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.login-container {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--turquoise-deep), var(--turquoise-primary));
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 20px;
  box-sizing: border-box;
  /* Protection supplémentaire pour s'assurer qu'aucun sidebar ne peut s'afficher */
  z-index: 9999;
  
  /* Pour les très petits écrans */
  @media (max-height: 600px) {
    align-items: flex-start;
    padding: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
  }
}

.login-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  opacity: 0.15;
  filter: blur(1px);
  /*transition: background-image 0.5s ease-in-out;*/
}

.login-card {
  display: flex;
  flex-direction: row;
  background-color: white;
  border-radius: 16px;
  width: 100%;
  max-width: 900px;
  overflow: hidden;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 2;
  animation: fadeIn 0.8s ease-out;
  min-height: 500px;
  max-height: 90vh;
  
  /* Écrans larges - Desktop */
  @media (min-width: 1200px) {
    max-width: 1000px;
    min-height: 600px;
  }
  
  /* Écrans moyens - Tablettes et petits laptops */
  @media (max-width: 1024px) {
    max-width: 95%;
    min-height: 550px;
  }
  
  /* Tablettes en portrait et mobiles larges */
  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 95%;
    min-height: auto;
    border-radius: 12px;
  }
  
  /* Mobiles */
  @media (max-width: 480px) {
    max-width: 98%;
    border-radius: 8px;
    margin: 10px 0;
  }
  
  /* Très petits écrans - hauteur limitée */
  @media (max-height: 600px) {
    min-height: auto;
    max-height: 85vh;
  }
  
  @media (max-height: 500px) {
    max-height: 90vh;
  }
  
  /* Écrans très étroits */
  @media (max-width: 360px) {
    max-width: 100%;
    border-radius: 0;
    box-shadow: none;
  }
}

.login-branding {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;
  background: linear-gradient(135deg, var(--turquoise-dark), var(--turquoise-light));
  color: white;
  text-align: center;
  position: relative;
  width: 40%;
  
  /* Écrans larges */
  @media (min-width: 1200px) {
    padding: 50px;
  }
  
  /* Tablettes */
  @media (max-width: 1024px) {
    padding: 35px;
  }
  
  /* Tablettes en portrait et mobiles larges */
  @media (max-width: 768px) {
    width: 100%;
    padding: 25px 20px;
    min-height: 200px;
  }
  
  /* Mobiles */
  @media (max-width: 480px) {
    padding: 20px 15px;
    min-height: 180px;
  }
  
  /* Très petits écrans */
  @media (max-width: 360px) {
    padding: 15px 10px;
    min-height: 160px;
  }
  
  /* Écrans avec hauteur limitée */
  @media (max-height: 600px) {
    padding: 15px;
    min-height: auto;
  }
  
  @media (max-height: 500px) {
    padding: 10px;
  }
}

.login-logo {
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
  object-fit: contain;
  /* Suppression du filtre pour conserver les couleurs originales */
  
  /* Adaptation responsive du logo */
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    margin-bottom: 15px;
  }
  
  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
    margin-bottom: 12px;
  }
  
  @media (max-height: 600px) {
    width: 60px;
    height: 60px;
    margin-bottom: 10px;
  }
}

.company-name {
  font-size: 32px;
  font-weight: 600;
  margin: 0 0 8px;
  
  /* Adaptation responsive du titre */
  @media (max-width: 768px) {
    font-size: 28px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
    margin: 0 0 6px;
  }
  
  @media (max-width: 360px) {
    font-size: 22px;
  }
  
  @media (max-height: 600px) {
    font-size: 26px;
    margin: 0 0 5px;
  }
}

.tagline {
  font-size: 16px;
  opacity: 0.9;
  
  /* Adaptation responsive du tagline */
  @media (max-width: 768px) {
    font-size: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
  
  @media (max-height: 600px) {
    font-size: 14px;
  }
}

.login-form {
  flex: 1;
  width: 60%;
  padding: 40px;
  background: white;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 0;
  
  /* Écrans larges */
  @media (min-width: 1200px) {
    padding: 50px;
  }
  
  /* Tablettes */
  @media (max-width: 1024px) {
    padding: 35px;
  }
  
  /* Tablettes en portrait et mobiles larges */
  @media (max-width: 768px) {
    width: 100%;
    padding: 30px 20px;
  }
  
  /* Mobiles */
  @media (max-width: 480px) {
    padding: 25px 15px;
  }
  
  /* Très petits écrans */
  @media (max-width: 360px) {
    padding: 20px 10px;
  }
  
  /* Écrans avec hauteur limitée */
  @media (max-height: 600px) {
    padding: 20px;
  }
  
  @media (max-height: 500px) {
    padding: 15px;
  }
}

.login-form h2 {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px;
  
  /* Adaptation responsive du titre du formulaire */
  @media (max-width: 768px) {
    font-size: 26px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
    margin: 0 0 6px;
  }
  
  @media (max-width: 360px) {
    font-size: 22px;
  }
  
  @media (max-height: 600px) {
    font-size: 24px;
    margin: 0 0 5px;
  }
}

.welcome-text {
  color: #666;
  margin-bottom: 30px;
  font-size: 15px;
  line-height: 1.5;
  
  /* Adaptation responsive du texte de bienvenue */
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 20px;
  }
  
  @media (max-height: 600px) {
    font-size: 13px;
    margin-bottom: 20px;
  }
}

.input-group {
  position: relative;
  margin-bottom: 24px;
  width: 100%;
  
  /* Ajustements pour mobiles */
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
}

.input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  display: flex;
  align-items: center;
}

.input-icon .material-icons {
  font-size: 20px;
}

.form-control {
  width: 100%;
  padding: 16px 16px 16px 48px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  color: #333;
  background-color: #f9fafb;
  transition: all 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--turquoise-primary);
  background-color: white;
  box-shadow: 0 0 0 3px rgba(32, 211, 208, 0.1);
}

.form-control::placeholder {
  color: #aaa;
}

.remember-forgot {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  font-size: 14px;
  
  /* Adapter pour les mobiles */
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
}


.remember-me {
  display: flex;
  align-items: center;
  color: #666;
  cursor: pointer;
}

.remember-me input {
  margin-right: 6px;
}

.forgot-password {
  color: var(--turquoise-primary);
  text-decoration: none;
  transition: color 0.2s;
}

.forgot-password:hover {
  color: var(--turquoise-dark);
  text-decoration: underline;
}

.error-message {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #fdeded;
  border-left: 4px solid #d32f2f;
  color: #d32f2f;
  border-radius: 4px;
  margin-bottom: 24px;
  font-size: 14px;
}

.error-message .material-icons {
  font-size: 20px;
  margin-right: 10px;
}

.login-button {
  padding: 14px;
  border-radius: 8px;
  background-color: var(--turquoise-primary);
  color: white;
  border: none;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.login-button:hover {
  background-color: var(--turquoise-dark);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(32, 211, 208, 0.3);
}

.login-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(32, 211, 208, 0.4);
  background-color: var(--turquoise-deep) !important;
}

.login-button:disabled {
  background-color: rgba(32, 211, 208, 0.6) !important;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-content .material-icons {
  margin-right: 8px;
  font-size: 20px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
  margin-right: 10px;
}

.login-footer {
  padding: 12px 40px;
  border-top: 1px solid #eaeaea;
  margin-top: auto;
  flex-shrink: 0;
}

.footer-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  font-size: 11px;
  color: #777;
}

.home-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid var(--turquoise-light);
  border-radius: 15px;
  color: var(--turquoise-primary);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  width: fit-content;
  margin: 0 auto;
}

.home-link:hover {
  background: var(--turquoise-very-light);
  border-color: var(--turquoise-primary);
  transform: translateY(-1px);
}

.home-link .material-icons {
  font-size: 14px;
}


@keyframes fadeIn {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (min-width: 768px) {
  .login-card {
    flex-direction: row;
  }
  
  .login-branding {
    display: flex;
    flex: 0 0 45%;
  }
  
  .login-form {
    flex: 0 0 55%;
  }
}

@media (max-width: 767px) {
  .login-card {
    max-width: 95%;
    height: auto;
  }
  
  .login-form {
    padding: 30px 20px;
  }
}
</style>